import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  Radio,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { linkPartner, searchPartner } from "../../../../../api/tenets";
import {
  emailValidationRegex,
  phoneNumberRegex,
} from "../../../../../constants/regexStrings";
import { SearchInputComponent } from "../../../../../common/components";
import "./linkPartnerPopup.scss";
import { debounce } from "lodash";
import MUIBoxTable from "../../../../../common/components/mui-table/mui-box-table";

interface LinkPartnerPopupProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  tenantId: number;
  setIsLoading: (isLoading: boolean) => void;
  setAlertMessage: any;
  onPartnerLinked: () => void;
}

type SearchType = "name" | "email" | "phone";

const SEARCH_CONFIG = [
  { key: "name", label: "Name", columnKey: "businessName" },
  { key: "email", label: "Email", columnKey: "email" },
  { key: "phone", label: "Phone", columnKey: "phoneNumber" },
];

export const LinkPartnerPopup: React.FC<LinkPartnerPopupProps> = ({
  isOpen,
  setIsOpen,
  tenantId,
  setIsLoading,
  setAlertMessage,
  onPartnerLinked,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchType, setSearchType] = useState<SearchType>("name");
  const [searchError, setSearchError] = useState("");
  const [searchResult, setSearchResult] = useState<any>(null);
  const [searchedValue, setSearchedValue] = useState("");
  const [isResetSearch, setIsResetSearch] = useState(false);

  const hasSearched = !!searchedValue;

  const columns = useMemo(
    () =>
      SEARCH_CONFIG.map((item) => ({
        label: item.label,
        key: item.columnKey,
      })),
    [],
  );

  const selectedConfig = useMemo(
    () => SEARCH_CONFIG.find((item) => item.key === searchType),
    [searchType],
  );

  const validateSearch = (value: string) => {
    if (searchType === "email" && !emailValidationRegex.test(value)) {
      return "Enter a valid email address.";
    }
    if (searchType === "phone" && !phoneNumberRegex.test(value)) {
      return "Enter valid phone number with country code.";
    }
    if (searchType === "name" && !value.trim()) {
      return "Name cannot be empty.";
    }
    return "";
  };

  const handlePartnerSearch = async (value: string) => {
    setSearchedValue(value);

    if (!value.trim()) {
      setSearchResult(null);
      return;
    }
    const error = validateSearch(value);
    setSearchError(error);
    if (error) return;
    setIsLoading(true);
    try {
      const response = await searchPartner({
        searchType,
        searchValue: value,
      });
      setSearchResult(response?.data?.data?.partner ?? null);
    } catch (error) {
      setAlertMessage({
        open: true,
        message:
          (error as any)?.response?.data?.message || "Failed to search partner",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      handlePartnerSearch(value);
    }, 500),
    [searchType],
  );

  const resetSearchState = () => {
    setSearchError("");
    setSearchResult(null);
    setSearchedValue("");
    setIsResetSearch((prev) => !prev);
  };

  const handleCloseLinkPartnerModal = () => {
    setIsOpen(false);
    resetSearchState();
  };

  const handleConfirmLink = async () => {
    if (!searchResult) return;
    try {
      setIsLoading(true);
      await linkPartner(tenantId, searchResult.id);
      handleCloseLinkPartnerModal();
      onPartnerLinked();
    } catch (error) {
      setAlertMessage({
        open: true,
        message:
          (error as any)?.response?.data?.message || "Failed to link partner",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const tableData = searchResult ? [searchResult] : [];

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseLinkPartnerModal}
      slotProps={{
        paper: {
          sx: {
            width: isMobile ? "90%" : "600px",
            maxWidth: "95vw",
            minHeight: "280px",
            borderBottomLeftRadius: "15px",
            borderBottomRightRadius: "15px",
            top: 0,
            margin: 0,
            position: "absolute",
          },
        },
      }}
      className="link-partner-dialog"
    >
      <DialogTitle className="link-partner-title">Link Partner</DialogTitle>
      <DialogContent className="link-partner-dialog-content">
        <Box mt={1}>Search partner by</Box>
        <RadioGroup row className="link-partner-radio-group">
          {SEARCH_CONFIG.map((item) => (
            <Box key={item.key} display="flex" alignItems="center">
              <Radio
                checked={searchType === item.key}
                onChange={() => {
                  setSearchType(item.key as SearchType);
                  resetSearchState();
                }}
              />
              <span>{item.label}</span>
            </Box>
          ))}
        </RadioGroup>
        <div className="link-partner-search">
          <SearchInputComponent
            placeholder={`Search by partner ${selectedConfig?.label.toLowerCase()}`}
            onSearch={debouncedSearch}
            isReset={isResetSearch}
          />
        </div>
        {searchError && (
          <Box sx={{ color: "red", mt: 1, fontSize: 13 }}>{searchError}</Box>
        )}
        {hasSearched && (
          <Box sx={{ mt: 2 }} className="link-partner-search-result-table">
            <MUIBoxTable
              columns={columns}
              data={tableData}
              noResultText="No partner found"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions className="link-partner-dialog-actions">
        <Button onClick={handleCloseLinkPartnerModal} className="cancel-button">
          Cancel
        </Button>
        <Button
          className="submit-button"
          disabled={!searchResult}
          onClick={handleConfirmLink}
        >
          Confirm Link
        </Button>
      </DialogActions>
    </Dialog>
  );
};
